<script setup lang="ts">
import {
  getCta,
  getElementUiObject,
  getHeadlineWithAllOptions,
  getLink,
  getMedia,
  getHtmlFromRichText,
  getHeadlineFontFromLegacy,
} from '@mop/cms/utils/utils';
import type { CmsUiImageText } from './uiCmsImageText';

defineOptions({
  name: 'UiCmsImageText',
});

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
});

const data = props.data;

const element: CmsUiImageText = getElementUiObject(data, {
  headline: getHeadlineWithAllOptions(data),
  headlineFontStyle: data.headlineFontStyle,
  headlineSize: data.headlineSize,
  headlineFont: data.headlineFont,
  subline: data.subline,
  text: getHtmlFromRichText(data.text),
  cta: getCta(data.cta?.[0]),
  link: getLink(data.link),
  textColor: data.textColor?.color,
  backgroundColor: data.backgroundColor?.color,
  textHorizontalAlignment: data.textHorizontalAlignment,
  textVerticalAlignment: data.textVerticalAlignment,
  showTextElementFirst: data.showTextElementFirst,
  showTextElementFirstMobile: data.showTextElementFirstMobile,
  hasIndentation: data.hasIndentation,
  media: getMedia(data.media?.[0], true),
  caption: data.caption,
  captionAlignment: data.captionAlignment,
});

if (element.media) {
  element.media.imageType = 'cms-two-column-no-ratio';
}

// Legacy fallback, remove after content fully migrated
if (!element.headlineFont) {
  element.headlineFont = getHeadlineFontFromLegacy(element.headlineFontStyle, element.headline?.level || 3);
}
</script>

<template>
  <UiNuxtLink
    v-storyblok-editable="element"
    v-bind="element.link"
    :class="[
      'ui-cms-image-text',
      {
        'ui-cms-image-text--text-first': element.showTextElementFirst,
        'ui-cms-image-text--text-first-mobile': element.showTextElementFirstMobile,
        'ui-cms-image-text--has-indentation': element.hasIndentation,
      },
    ]"
    :data-track-element-item="element.media?.trackingId"
    :style="{
      '--color': element.textColor,
      '--background-color': element.backgroundColor,
    }"
  >
    <div class="ui-cms-image-text__image">
      <UiCmsMedia v-if="element.media" :data="element.media" :lazy="lazyLoad" />
      <div
        v-if="element.caption"
        :class="[
          'ui-cms-image-text__image-caption',
          {
            'ui-cms-image-text__image-caption--align-right': element.captionAlignment === 'right',
          },
        ]"
      >
        {{ element.caption }}
      </div>
    </div>
    <div class="ui-cms-image-text__content-wrapper">
      <div
        :class="[
          'ui-cms-image-text__content',
          element.textHorizontalAlignment ? `ui-cms-image-text__content--align-${element.textHorizontalAlignment}` : '',
          element.textVerticalAlignment
            ? `ui-cms-image-text__content--vertical-align-${element.textVerticalAlignment}`
            : '',
        ]"
      >
        <div>
          <Ui2Heading
            v-if="element.headline.text"
            :class="[
              'ui-cms-image-text__content-headline',
              {
                'ui-cms-image-text__content-headline--uppercase': element.headline.isUppercase,
              },
            ]"
            :font="element.headlineFont"
            :is-uppercase="element.headline.isUppercase"
            :level="element.headline.level ?? 2"
          >
            {{ element.headline.text }}
          </Ui2Heading>
          <div v-if="element.subline" class="ui-cms-image-text__content-subline">
            {{ element.subline }}
          </div>
          <div v-if="element.text" v-html="element.text" />

          <UiCmsCta v-if="element.cta" :data="element.cta" no-padding class="ui-cms-image-text__content-cta" />
        </div>
      </div>
    </div>
  </UiNuxtLink>
</template>

<style lang="scss" scoped>
.ui-cms-image-text {
  display: flex;
  flex-wrap: wrap;
  color: var(--color);
}

.ui-cms-image-text--has-indentation {
  padding: 0 $ui-global-padding;
}

.ui-cms-image-text__image,
.ui-cms-image-text__content-wrapper {
  flex-basis: 50%;
  width: 50%;
  background-color: var(--background-color);

  @include apply-upto(small) {
    flex-basis: 100%;
    width: 100%;
  }
}

.ui-cms-image-text__image-caption {
  padding: $space-8 $ui-global-padding;
}

.ui-cms-image-text--has-indentation {
  .ui-cms-image-text__image-caption {
    padding: $space-8 0;
  }
}

.ui-cms-image-text__image-caption--align-right {
  text-align: right;
}

.ui-cms-image-text--text-first {
  .ui-cms-image-text__content-wrapper {
    order: 1;
  }

  .ui-cms-image-text__image {
    order: 2;

    @include apply-upto(small) {
      order: 1;
    }
  }
}

.ui-cms-image-text--text-first-mobile {
  @include apply-upto(small) {
    .ui-cms-image-text__content-wrapper {
      order: 1;
    }

    .ui-cms-image-text__image {
      order: 2;
    }
  }
}

.ui-cms-image-text__content {
  padding: 0 $ui-global-padding;
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;

  @include apply-upto(small) {
    padding: $ui-global-padding;
  }
}

.ui-cms-image-text__content--vertical-align-top {
  align-items: flex-start;
}

.ui-cms-image-text__content--vertical-align-bottom {
  align-items: flex-end;
}

.ui-cms-image-text__content-subline {
  @include v2-text-style('xl');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('lg');
  }
}

.ui-cms-image-text__content-cta {
  margin-top: $space-16;
}
</style>
